import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, getFinancialYear, getFinancialYearStart, getFinancialYearEnd } from '@/lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, DollarSign, Receipt, Landmark, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

function getFYOptions() {
  const now = new Date();
  const currentFY = getFinancialYear(now);
  const options: { label: string; startYear: number }[] = [];
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  for (let i = 0; i < 5; i++) {
    const y = startYear - i;
    options.push({ label: `${y}-${(y + 1).toString().slice(-2)}`, startYear: y });
  }
  return options;
}

export default function TaxWizardPage() {
  const { data } = useAuth();
  const fyOptions = useMemo(() => getFYOptions(), []);
  const [selectedFY, setSelectedFY] = useState(fyOptions[0].label);

  const selectedStartYear = fyOptions.find(o => o.label === selectedFY)!.startYear;
  const fyStart = new Date(selectedStartYear, 6, 1);
  const fyEnd = new Date(selectedStartYear + 1, 5, 30);

  const stats = useMemo(() => {
    const txns = data.transactions.filter(t => {
      const d = new Date(t.date);
      return d >= fyStart && d <= fyEnd;
    });

    const totalIncome = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const totalExpenses = txns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    const totalSuper = txns.filter(t => t.type === 'income' && t.superContribution).reduce((s, t) => s + (t.superContribution ?? 0), 0);

    const deductibleTxns = txns.filter(t => t.taxDeductible);
    const totalDeductible = deductibleTxns.reduce((s, t) => s + t.amount, 0);

    const deductibleByCategory: Record<string, { total: number; count: number }> = {};
    for (const t of deductibleTxns) {
      if (!deductibleByCategory[t.category]) {
        deductibleByCategory[t.category] = { total: 0, count: 0 };
      }
      deductibleByCategory[t.category].total += t.amount;
      deductibleByCategory[t.category].count += 1;
    }

    const sortedCategories = Object.entries(deductibleByCategory)
      .sort((a, b) => b[1].total - a[1].total);

    return { totalIncome, totalExpenses, totalSuper, totalDeductible, sortedCategories, deductibleTxns, txnCount: txns.length };
  }, [data.transactions, fyStart, fyEnd]);

  const [step, setStep] = useState(0);
  const steps = ['Overview', 'Deductions', 'Summary & Export'];

  async function generatePDF() {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF();
    const fyLabel = `FY ${selectedFY}`;
    const period = `1 July ${selectedStartYear} – 30 June ${selectedStartYear + 1}`;

    // Header
    doc.setFontSize(20);
    doc.text('AussiBooks Tax Summary', 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`${fyLabel}  •  ${period}`, 14, 30);
    doc.text(`Generated ${new Date().toLocaleDateString('en-AU')}`, 14, 36);

    // Income & Expenses overview
    doc.setTextColor(0);
    doc.setFontSize(14);
    doc.text('Income & Expenses', 14, 50);

    autoTable(doc, {
      startY: 54,
      head: [['Item', 'Amount']],
      body: [
        ['Total Income', formatAUD(stats.totalIncome)],
        ['Total Expenses', formatAUD(stats.totalExpenses)],
        ['Net Income', formatAUD(stats.totalIncome - stats.totalExpenses)],
        ['Super Contributions (employer)', formatAUD(stats.totalSuper)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [45, 140, 120] },
    });

    // Deductible expenses
    const afterFirstTable = (doc as any).lastAutoTable.finalY + 12;
    doc.setFontSize(14);
    doc.text('Tax-Deductible Expenses by Category', 14, afterFirstTable);

    if (stats.sortedCategories.length > 0) {
      autoTable(doc, {
        startY: afterFirstTable + 4,
        head: [['Category', 'Items', 'Total']],
        body: stats.sortedCategories.map(([cat, v]) => [cat, v.count.toString(), formatAUD(v.total)]),
        foot: [['Total Deductible', '', formatAUD(stats.totalDeductible)]],
        theme: 'striped',
        headStyles: { fillColor: [45, 140, 120] },
        footStyles: { fillColor: [230, 245, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      });
    } else {
      doc.setFontSize(11);
      doc.text('No tax-deductible expenses recorded for this period.', 14, afterFirstTable + 8);
    }

    // Disclaimer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(130);
    doc.text('This report is for informational purposes only and does not constitute tax advice.', 14, pageHeight - 15);
    doc.text('Please consult a registered tax agent for professional advice.', 14, pageHeight - 10);

    doc.save(`AussiBooks_Tax_Summary_FY${selectedFY}.pdf`);
  }

  return (
    <div className="px-4 pt-6 pb-24">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            Tax Time Wizard
          </h1>
          <p className="text-sm text-muted-foreground">End-of-year tax summary for myTax</p>
        </div>
        <Select value={selectedFY} onValueChange={setSelectedFY}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fyOptions.map(o => (
              <SelectItem key={o.label} value={o.label}>FY {o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Step indicator */}
      <div className="mb-6 flex gap-1">
        {steps.map((s, i) => (
          <button
            key={s}
            onClick={() => setStep(i)}
            className={cn(
              'flex-1 rounded-full py-1.5 text-xs font-medium transition-colors',
              i === step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            )}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Step 0: Overview */}
      {step === 0 && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground mb-3">
            FY {selectedFY} · {stats.txnCount} transactions recorded
          </p>

          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-success/10">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Income</p>
                <p className="font-display text-xl font-bold">{formatAUD(stats.totalIncome)}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
                <Receipt className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="font-display text-xl font-bold">{formatAUD(stats.totalExpenses)}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Landmark className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Super Contributions</p>
                <p className="font-display text-xl font-bold">{formatAUD(stats.totalSuper)}</p>
              </div>
            </CardContent>
          </Card>

          <Button className="mt-4 w-full gap-2" onClick={() => setStep(1)}>
            View Deductions <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Step 1: Deductions breakdown */}
      {step === 1 && (
        <div className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Tax-Deductible Expenses</CardTitle>
              <p className="text-sm text-muted-foreground">
                {stats.deductibleTxns.length} deductible items · {formatAUD(stats.totalDeductible)} total
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.sortedCategories.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No tax-deductible expenses found for this financial year.
                </p>
              ) : (
                stats.sortedCategories.map(([cat, v]) => (
                  <div key={cat} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">{cat}</p>
                      <p className="text-xs text-muted-foreground">{v.count} item{v.count !== 1 ? 's' : ''}</p>
                    </div>
                    <Badge variant="secondary" className="font-display font-semibold">
                      {formatAUD(v.total)}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Button className="mt-4 w-full gap-2" onClick={() => setStep(2)}>
            View Summary & Export <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Step 2: Summary & PDF export */}
      {step === 2 && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">FY {selectedFY} Tax Summary</CardTitle>
              <p className="text-xs text-muted-foreground">
                1 July {selectedStartYear} – 30 June {selectedStartYear + 1}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Gross Income</span><span className="font-semibold">{formatAUD(stats.totalIncome)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Expenses</span><span className="font-semibold">{formatAUD(stats.totalExpenses)}</span></div>
                <Separator />
                <div className="flex justify-between"><span className="text-muted-foreground">Net Income</span><span className="font-display font-bold">{formatAUD(stats.totalIncome - stats.totalExpenses)}</span></div>
                <Separator />
                <div className="flex justify-between"><span className="text-muted-foreground">Super Contributions</span><span className="font-semibold">{formatAUD(stats.totalSuper)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax-Deductible Total</span><span className="font-semibold text-primary">{formatAUD(stats.totalDeductible)}</span></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-dashed border-primary/30 bg-primary/5">
            <CardContent className="p-4 text-center">
              <p className="mb-3 text-sm text-muted-foreground">
                Download a PDF report with all your income, expenses, and deductions ready for myTax or your accountant.
              </p>
              <Button className="gap-2" onClick={generatePDF}>
                <Download className="h-4 w-4" />
                Download PDF Report
              </Button>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground text-center mt-2">
            This report is for informational purposes only. Please consult a registered tax agent for professional advice.
          </p>
        </div>
      )}
    </div>
  );
}
